// React & React Hooks
import { useEffect, useState } from "react";

// Third-party libraries
import moment from "moment/moment";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

// MUI components
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// MUI icons
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditIcon from "@mui/icons-material/Edit";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassBottomRounded from "@mui/icons-material/HourglassBottomRounded";
import UndoIcon from "@mui/icons-material/Undo";
import AccountBalanceWalletRounded from "@mui/icons-material/AccountBalanceWalletRounded";
import BlockRounded from "@mui/icons-material/BlockRounded";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Custom hooks
import useDebounce from "../custom-hook/useDebounce";

const DataCardGrid = ({
  initialData = [],
  columns = [],
  title = null,
  defaultItemsPerPage = 8,
  defaultColumnsPerGrid = 3,
  enableExport = true,
  enableFilters = true,
  enablePagination = true,
  enableSearch = true,
  enableSorting = true,
  enableGridConfig = true,
  onDataChange = null,
  defaultSortKey = null,
  defaultSortDirection = null,
  exportFileName = "data.xlsx",
  uniqueId = "card-grid",
  actionButtons = [],
  onFetchData = null,
  initialVisibleRows = null,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState(initialData);
  const [search, setSearch] = useState("");
  const [isSearchText, setIsSearchText] = useState(false);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: defaultSortKey,
    direction: defaultSortDirection,
  });
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const [columnsPerGrid, setColumnsPerGrid] = useState(defaultColumnsPerGrid);
  const [currentPage, setCurrentPage] = useState(1);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activePopover, setActivePopover] = useState(null);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFirstloading, setIsFirstLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedCards, setExpandedCards] = useState({});
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  const hasLimitedRows =
    typeof initialVisibleRows === "number" &&
    initialVisibleRows > 0 &&
    columns.length > initialVisibleRows;

  const getVisibleRowsForCard = (cardIndex) => {
    const isExpanded = expandedCards[cardIndex];
    return hasLimitedRows && !isExpanded
      ? columns.slice(0, initialVisibleRows)
      : columns;
  };

  const toggleCardExpansion = (cardIndex) => {
    setExpandedCards((prev) => ({
      ...prev,
      [cardIndex]: !prev[cardIndex],
    }));
  };

  useEffect(() => {
    setExpandedCards({});
  }, [currentPage, filters, search, sortConfig]);

  const isColumnFilterable = (columnKey) => {
    const column = columns.find((col) => col.key === columnKey);
    return !!column?.isFilterVisible;
  };

  const getUniqueValues = (columnKey) => {
    const uniqueValues = [...new Set(data.map((item) => item[columnKey]))];
    return uniqueValues.sort((a, b) => {
      if (typeof a === "string" && typeof b === "string") {
        return a.localeCompare(b);
      }
      return a - b;
    });
  };

  const formatValue = (value, columnType) => {
    if (value == null) return "";

    if (columnType === "date") {
      const date = moment(value);
      return date.isValid() ? date.format("MM/DD/YYYY") : value.toString();
    } else if (columnType === "money") {
      return value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
    }
    return value.toString();
  };

  const debouncedSearch = useDebounce(search, 1000);
  const debouncedFilter = useDebounce(filters, 700);

  useEffect(() => {
    if (!onFetchData) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await onFetchData({
          page: currentPage,
          pageSize: itemsPerPage,
          search,
          filters,
          sortKey: sortConfig.key,
          sortDirection: sortConfig.direction,
        });

        setData(result.data);
        setTotalCount(result.totalCount);
        onDataChange?.(result.data);
      } catch (error) {
        console.error("Data fetch error:", error);
      } finally {
        setLoading(false);
        setIsFirstLoading(true);
      }
    };
    fetchData();
  }, [currentPage, itemsPerPage, isSearchText, sortConfig, onDataChange]);

  useEffect(() => {
    if (initialData.length > 0 && !onFetchData) {
      setData(initialData);
      setTotalCount(initialData.length);
    }
  }, [initialData, onFetchData]);

  const generateExcelFile = (dataToExport, fileName) => {
    const filteredData = dataToExport.map((item) =>
      columns.reduce((acc, { key, label }) => {
        acc[label] = item[key];
        return acc;
      }, {})
    );
    
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, title || "Data");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, fileName);
  };

  const handleExportCurrentPage = () => {
    generateExcelFile(data, exportFileName);
    setExportMenuAnchor(null);
  };

  const handleExportAllPages = async () => {
    if (!onFetchData) {
      handleExportCurrentPage();
      return;
    }

    setExportMenuAnchor(null);

    try {
      const allDataResult = await onFetchData({
        page: 1,
        pageSize: totalCount, 
        search,
        filters,
        sortKey: sortConfig.key,
        sortDirection: sortConfig.direction,
      });

      const allData = allDataResult.data || [];
      const fileName = exportFileName.replace('.xlsx', '_all_pages.xlsx');
      generateExcelFile(allData, fileName);
    } catch (error) {
      console.error("Export all pages error:", error);
      handleExportCurrentPage();
    }
  };

  const handleExportMenuClick = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  useEffect(() => {
    if (isFirstloading) {
      setCurrentPage(1);
      setIsSearchText(!isSearchText);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (isFirstloading) {
      setCurrentPage(1);
      setIsSearchText(!isSearchText);
    }
  }, [debouncedFilter]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setCurrentPage(1);
  };

  const handleClearFilter = (key) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const getFilterableColumns = () => {
    return columns.filter((col) => isColumnFilterable(col.key));
  };

  const totalPages = Math.max(
    1,
    Math.ceil(totalCount / Math.max(1, itemsPerPage))
  );

  const createGridStructure = () => {
    const rows = Math.ceil(data.length / columnsPerGrid);
    const grid = [];
    for (let rowIndex = 0; rowIndex < rows; rowIndex++) {
      const rowItems = [];
      for (let colIndex = 0; colIndex < columnsPerGrid; colIndex++) {
        const itemIndex = rowIndex * columnsPerGrid + colIndex;
        if (itemIndex < data.length) {
          rowItems.push({ item: data[itemIndex], originalIndex: itemIndex });
        }
      }
      grid.push(rowItems);
    }

    return grid;
  };

  const getIconComponent = (iconName, color) => {
    const icons = {
      eye: <VisibilityIcon />,
      pencil: <EditIcon />,
      trash: <DeleteIcon />,
      checkBoxIcon: (
        <CheckIcon
          sx={{
            background: `var(--${color})`,
            padding: 0.3,
            minWidth: "26px",
            minHeight: "26px",
            borderRadius: "8px",
          }}
        />
      ),
    };
    return icons[iconName] || null;
  };

  const StatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return (
          <HourglassBottomRounded
            color="warning"
            sx={{
              padding: 0.3,
              minWidth: "26px",
              minHeight: "26px",
              borderRadius: "8px",
            }}
          />
        );
      case "paid" || "completed":
        return (
          <CheckCircleIcon
            color="success"
            sx={{
              padding: 0.3,
              minWidth: "26px",
              minHeight: "26px",
              borderRadius: "8px",
            }}
          />
        );
      case "canceled":
        return (
          <CancelIcon
            color="error"
            sx={{
              padding: 0.3,
              minWidth: "26px",
              minHeight: "26px",
              borderRadius: "8px",
            }}
          />
        );
      case "refunded":
        return (
          <UndoIcon
            color="blue"
            sx={{
              padding: 0.3,
              minWidth: "26px",
              minHeight: "26px",
              borderRadius: "8px",
            }}
          />
        );
      case "on hold":
        return (
          <AccountBalanceWalletRounded
            color="error"
            sx={{
              padding: 0.3,
              minWidth: "26px",
              minHeight: "26px",
              borderRadius: "8px",
            }}
          />
        );
      case "inactive":
        return (
          <BlockRounded
            color="disabled"
            sx={{
              padding: 0.3,
              minWidth: "26px",
              minHeight: "26px",
              borderRadius: "8px",
            }}
          />
        );
      case "active":
        return (
          <AccountBalanceWalletRounded
            color="success"
            sx={{
              padding: 0.3,
              minWidth: "26px",
              minHeight: "26px",
              borderRadius: "8px",
            }}
          />
        );
      case "deleted":
        return (
          <HighlightOffIcon
            color="error"
            sx={{
              padding: 0.3,
              minWidth: "26px",
              minHeight: "26px",
              borderRadius: "8px",
            }}
          />
        );
      default:
        return null;
    }
  };

  const grid = createGridStructure();

  // const handleFilterClick = (event, column) => {
  //   setAnchorEl(event.currentTarget);
  //   setActivePopover(column.key);
  // };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setActivePopover(null);
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? `popover-${uniqueId}-${activePopover}` : undefined;

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };
  const columnOptions = [1, 2, 3, 4];
  const itemsPerPageOptions = [4, 8, 12, 16, 20];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={12} width={"100%"}>
        <Paper elevation={0} sx={{ background: "transparent" }}>
          {title && (
            <Typography variant="h5" sx={{ mb: 3 }}>
              {title}
            </Typography>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
            {enableSearch && (
              <Stack
                className="cm-search-export"
                sx={{ display: "flex", flex: 1 }}
              >
                <Card className="search-icon">
                  <SearchIcon sx={{ color: "var(--black)" }} />
                </Card>
                <TextField
                  fullWidth
                  placeholder={t("Common.Search")}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  size="small"
                  variant="outlined"
                />
                {enableExport && (
                  <>
                    <Button
                      className="export"
                      variant="contained"
                      disabled={!data?.length > 0}
                      color="secondary"
                      onClick={handleExportMenuClick}
                      startIcon={
                        <DescriptionOutlinedIcon sx={{ fontSize: "24px" }} />
                      }
                      sx={{}}
                    >
                      {t("DataCardGrid.Export")}
                    </Button>
                    <Menu
                      anchorEl={exportMenuAnchor}
                      open={Boolean(exportMenuAnchor)}
                      onClose={handleExportMenuClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                    >
                      <MenuItem onClick={handleExportCurrentPage}>
                        <DescriptionOutlinedIcon sx={{ mr: 1 }} />
                        {t("DataCardGrid.Export Current Page")}
                      </MenuItem>
                      <MenuItem
                        onClick={handleExportAllPages}
                        disabled={!onFetchData}
                      >
                        <DescriptionOutlinedIcon sx={{ mr: 1 }} />
                        {t("DataCardGrid.Export All Pages")}
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Stack>
            )}
          </Box>
          {enableGridConfig && (
            <Box sx={{ mb: 3, display: "flex", gap: 3 }}>
              <FormControl
                className="cm-select-dropdown"
                sx={{
                  minWidth: 180,
                  display: {
                    xs: "none !important",
                    md: "inline-flex !important",
                  },
                }}
              >
                <InputLabel id="columns-per-grid-label">
                  {t("DataCardGrid.Columns Per Grid")}
                </InputLabel>
                <Select
                  labelId="columns-per-grid-label"
                  id="columns-per-grid"
                  value={columnsPerGrid}
                  label={t("DataCardGrid.Columns Per Grid")}
                  onChange={(e) => setColumnsPerGrid(Number(e.target.value))}
                  size="small"
                >
                  {columnOptions.map((val) => (
                    <MenuItem key={val} value={val}>
                      {val}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack
                sx={(theme) => ({
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  alignItems: { xs: "flex-end", sm: "center" },
                  justifyContent: "space-between",

                  [theme.breakpoints.down(350)]: {
                    flexWrap: "wrap",
                  },
                })}
              >
                <FormControl
                  className="cm-select-dropdown"
                  sx={{ minWidth: 180 }}
                >
                  <InputLabel id="items-per-page-label">
                    {t("DataCardGrid.Items Per Page")}
                  </InputLabel>
                  <Select
                    labelId="items-per-page-label"
                    id="items-per-page"
                    value={itemsPerPage}
                    label={t("DataCardGrid.Items Per Page")}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    size="small"
                  >
                    {itemsPerPageOptions.map((val) => (
                      <MenuItem key={val} value={val}>
                        {val}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {enableFilters && getFilterableColumns().length > 0 && (
                  <>
                  <Stack direction={"row"} gap={"10px"} alignItems={"center"} flexWrap={"wrap"}>
                      <Typography color="secondary.text" variant="body2">
                      {totalCount || 0}{" "}
                      {t("Dashboard.InTotal")}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => setShowFilterBar(!showFilterBar)}
                      color="secondary"
                      size="large"
                      sx={{
                        px: 1.2,
                        py: 1.2,
                        margin: 0,
                        "& .MuiButton-startIcon": {
                          margin: 0,
                        },
                      }}
                      startIcon={
                        <FilterAltOutlinedIcon
                          sx={{ width: "30px", height: "30px" }}
                        />
                      }
                    >
                      {/* {t(
                      showFilterBar
                        ? 'DataCardGrid.Hide Filters'
                        : 'DataCardGrid.Show Filters'
                    )} */}
                    </Button>
                  </Stack>                  
                  </>
                )}
              </Stack>
            </Box>
          )}

          {/* Filter Bar - Only shows filterable columns */}
          {enableFilters &&
            showFilterBar &&
            getFilterableColumns().length > 0 && (
              <Paper
                elevation={0}
                sx={{ mb: "20px", background: "transparent" }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat( auto-fit, minmax(100%, 1fr) )",
                      sm: "repeat( auto-fit, minmax(24%, 1fr) )",
                      lg: "repeat( auto-fit, minmax(19%, 1fr) )",
                    },
                    gap: "20px",
                    flexDirection: { xs: "column", sm: "row" },
                    overflow: {
                      xs: "auto",
                      sm: "initial",
                    },
                    maxHeight: {
                      xs: "450px",
                      sm: "initial",
                    },
                  }}
                >
                  {getFilterableColumns().map((column) => (
                    <Box
                      key={column.key}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minWidth: { xs: "100px", sm: "120px" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          alignItems: "start",
                          flexDirection: "column",
                          p: "10px",
                          background: "var(--black)",
                          borderRadius: "10px",
                          gap: 0.5,
                          minWidth: { xs: "100px", sm: "120px" },
                        }}
                      >
                        <Typography
                          sx={{
                            cursor: enableSorting ? "pointer" : "default",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                          onClick={() =>
                            enableSorting && handleSort(column.key)
                          }
                        >
                          {column.label}
                          {sortConfig.key === column.key &&
                            (sortConfig.direction === "asc" ? (
                              <ArrowUpwardIcon
                                color="primary"
                                fontSize="small"
                              />
                            ) : (
                              <ArrowDownwardIcon
                                color="primary"
                                fontSize="small"
                              />
                            ))}
                        </Typography>
                        {column.type !== "date" && (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            width={"100%"}
                          >
                            <TextField
                              fullWidth
                              sx={{
                                "& .MuiInputBase-root": {
                                  backgroundColor: "var(--white)",
                                  color: "var(--black)",
                                  minHeight: "50px",
                                  borderRadius: "10px",
                                  minWidth: { xs: "100px", sm: "120px" },
                                },
                              }}
                              size="small"
                              type={
                                column.type === "number" ? "number" : "text"
                              }
                              placeholder={t("DataCardGrid.Filter value")}
                              value={filters[column.key] || ""}
                              onChange={(e) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  [column.key]: e.target.value,
                                }))
                              }
                            />
                            <Button
                              variant={
                                filters[column.key] ? "contained" : "contained"
                              }
                              color="secondary"
                              onClick={() => {
                                if (filters[column.key]) {
                                  handleClearFilter(column.key);
                                }
                              }}
                              sx={{
                                px: 1.2,
                                py: 1.2,
                                background: "var(--input-gradient)",
                                margin: 0,
                                "& .MuiButton-startIcon": {
                                  margin: 0,
                                },
                              }}
                            >
                              {filters[column.key] ? (
                                <FilterAltOffIcon
                                  sx={{
                                    width: "30px",
                                    height: "30px",
                                  }}
                                />
                              ) : (
                                <FilterAltOutlinedIcon
                                  sx={{ width: "30px", height: "30px" }}
                                />
                              )}
                            </Button>
                          </Stack>
                        )}
                        {/* {column.type !== "date" && <IconButton
                          size="small"
                          color={filters[column.key] ? "primary" : "default"}
                          aria-describedby={popoverId}
                          onClick={(e) => handleFilterClick(e, column)}
                        >
                          <FilterListIcon fontSize="small" />
                        </IconButton>} */}
                        {column.type === "date" && (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            width={"100%"}
                          >
                            <DatePicker
                              className="cm-date-picker"
                              format="MM/dd/yyyy"
                              value={
                                filters[column.key]
                                  ? new Date(filters[column.key])
                                  : null
                              }
                              onChange={(value) => {
                                if (value instanceof Date && !isNaN(value)) {
                                  const localDateTime = value.toDateString();
                                  handleFilterChange(column.key, localDateTime);
                                } else if (value === null) {
                                  handleClearFilter(column.key);
                                }
                              }}
                              slotProps={{
                                field: {
                                  clearable: false,
                                  onClear: () => handleClearFilter(column.key),
                                  placeholder: t("DataCardGrid.Filter date"),
                                },
                              }}
                              sx={{
                                width: "100%",
                                "& .MuiPickersInputBase-root ": {
                                  backgroundColor: "var(--white)",
                                  color: "var(--black)",
                                  minHeight: "50px",
                                  borderRadius: "10px",
                                },
                                "& .MuiPickersSectionList-root": {
                                  padding: "13.5px 0",
                                },
                              }}
                            />
                            <Button
                              variant={
                                filters[column.key] ? "contained" : "contained"
                              }
                              color="secondary"
                              onClick={() => {
                                if (filters[column.key]) {
                                  handleClearFilter(column.key);
                                }
                              }}
                              sx={{
                                px: 1.2,
                                py: 1.2,
                                margin: 0,
                                background: "var(--input-gradient)",
                                "& .MuiButton-startIcon": {
                                  margin: 0,
                                },
                              }}
                            >
                              {filters[column.key] ? (
                                <FilterAltOffIcon
                                  sx={{ width: "30px", height: "30px" }}
                                />
                              ) : (
                                <FilterAltOutlinedIcon
                                  sx={{ width: "30px", height: "30px" }}
                                />
                              )}
                            </Button>
                          </Stack>
                        )}
                        <Popover
                          id={popoverId}
                          open={activePopover === column.key}
                          anchorEl={anchorEl}
                          onClose={handleClosePopover}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                          sx={{ minWidth: "250px" }}
                        >
                          <Box sx={{ px: 2, pb: 2 }}>
                            {column.isDropDown && (
                              <>
                                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      handleClearFilter(column.key)
                                    }
                                  >
                                    {t("Common.Clear")}
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handleClosePopover}
                                  >
                                    {t("DataCardGrid.Apply")}
                                  </Button>
                                </Box>
                                <Divider />
                                <Box
                                  sx={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                    mt: 2,
                                  }}
                                >
                                  {getUniqueValues(column.key).map(
                                    (value, idx) => (
                                      <Typography
                                        key={idx}
                                        sx={{
                                          py: 1,
                                          px: 2,
                                          cursor: "pointer",
                                          "&:hover": {
                                            bgcolor: "action.hover",
                                          },
                                        }}
                                        onClick={() => {
                                          handleFilterChange(
                                            column.key,
                                            value.toString()
                                          );
                                          handleClosePopover();
                                        }}
                                      >
                                        {column.type === "date" && value
                                          ? formatValue(value, "date")
                                          : value}
                                      </Typography>
                                    )
                                  )}
                                </Box>
                              </>
                            )}
                          </Box>
                        </Popover>
                      </Box>
                      {/* {filters[column.key] && (
                        <Box
                          sx={{
                            px: 2,
                            py: 1,
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            mt: "10px",
                            mx: "10px",
                            borderRadius: "10px",
                            minWidth: { xs: "100px", sm: "120px" },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            {t("DataCardGrid.Filter")}:{" "}
                            {column.type === "date" && filters[column.key]
                              ? formatValue(filters[column.key], "date")
                              : filters[column.key]}
                            <IconButton
                              size="small"
                              sx={{ ml: 1, color: "inherit" }}
                              onClick={() => handleClearFilter(column.key)}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Typography>
                        </Box>
                      )} */}
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}

          {/* Active Filters Summary (when filter bar is hidden) */}
          {enableFilters &&
            !showFilterBar &&
            Object.keys(filters).length > 0 && (
              <Paper sx={{ mb: 3, p: 2, bgcolor: "action.hover" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: "bold", mr: 2 }}>
                    {t("DataCardGrid.Active Filters")}
                  </Typography>
                  {Object.entries(filters).map(([key, value]) => {
                    const column = columns.find((col) => col.key === key);
                    return (
                      <Chip
                        key={key}
                        label={`${column?.label}: ${value}`}
                        color="primary"
                        onDelete={() => handleClearFilter(key)}
                        size="small"
                      />
                    );
                  })}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setFilters({})}
                    sx={{ ml: 1 }}
                  >
                    {t("DataCardGrid.Clear All")}
                  </Button>
                </Box>
              </Paper>
            )}
          {loading && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              my={4}
            >
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t("DataCardGrid.Loading")}
              </Typography>
            </Box>
          )}
          {/* Grid Layout */}
          {!loading && (
            <Box className="grid-container">
              {grid.map((row, rowIndex) => {
                const width = 12 / columnsPerGrid;
                return (
                  <Grid
                    container
                    key={rowIndex}
                    spacing={"20px"}
                    sx={{ mb: 3 }}
                  >
                    {row.map(({ item, originalIndex }, colIndex) => {
                      const cardIndex = originalIndex;
                      const visibleRows = getVisibleRowsForCard(cardIndex);
                      const isExpanded = expandedCards[cardIndex];
                      return (
                        <Grid
                          key={colIndex}
                          item
                          xs={12}
                          sx={{ flex: { xs: "auto", sm: "1 1 0" } }}
                          sm={columnsPerGrid}
                          md={columnsPerGrid}
                        >
                          <Card
                            sx={(theme) => ({
                              background: theme.palette.secondary.lightest,
                              borderRadius: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 0,
                              width: "100%",
                              flexDirection: "column",
                              height: "100%",
                              padding: "20px",
                            })}
                          >
                            <TableContainer
                              sx={{ background: "transparent" }}
                              component={Paper}
                              elevation={0}
                              className="two-table"
                            >
                              <Table aria-label="simple table">
                                <TableBody>
                                  {visibleRows.map((col, dataIndex) => (
                                    <TableRow
                                      key={dataIndex}
                                      sx={{
                                        "&:last-child td, &:last-child th": {
                                          border: 0,
                                        },
                                      }}
                                    >
                                      <TableCell className="label-data">
                                        {col.label}
                                      </TableCell>
                                      <TableCell align="right">
                                        <Stack
                                          direction={"row"}
                                          display={"inline-flex"}
                                          alignItems={"center"}
                                          gap={1}
                                          width={{ xs: "100%", sm: "auto" }}
                                          justifyContent={"space-between"}
                                        >
                                          {formatValue(item[col.key], col.type)}
                                          {col.icon &&
                                            getIconComponent(col.icon, "green")}
                                          {col.statusIcon &&
                                            StatusIcon(
                                              item[col.key].toLowerCase()
                                            )}
                                        </Stack>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            {hasLimitedRows && (
                              <Stack width="100%" alignItems="center" pt={1}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => toggleCardExpansion(cardIndex)}
                                  sx={{
                                    px: 2.5,
                                    py: 0.5,
                                    color: "var(--primary)",
                                    backgroundColor: "var(--primary-darker)",
                                  }}
                                >
                                  {isExpanded
                                    ? t("Common.viewLess")
                                    : t("Common.viewMore")}
                                </Button>
                              </Stack>
                            )}
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              flexWrap={"wrap"}
                              gap={"20px"}
                              pt={2}
                              width={"100%"}
                              justifyContent={"center"}
                            >
                              {actionButtons &&
                                actionButtons.length > 0 &&
                                actionButtons
                                  .filter(
                                    (x) =>
                                      ![
                                        "trash",
                                        "eye",
                                        "default",
                                        "pencil",
                                      ].includes(x.icon)
                                  )
                                  ?.map(
                                    ({
                                      id,
                                      label,
                                      color = "primary",
                                      onClick,
                                      disabled,
                                      icon,
                                    }) => (
                                      <Stack key={id}>
                                        <Button
                                          key={id}
                                          size="small"
                                          variant="contained"
                                          color="secondary"
                                          onClick={() => onClick(item)}
                                          sx={{
                                            px: 3,
                                            backgroundColor: `var(--${color})`,
                                          }}
                                          disabled={
                                            disabled ? disabled(item) : false
                                          }
                                          startIcon={
                                            icon
                                              ? getIconComponent(icon, color)
                                              : null
                                          }
                                        >
                                          {label}
                                        </Button>
                                      </Stack>
                                    )
                                  )}
                              {actionButtons &&
                                actionButtons.length > 0 &&
                                actionButtons
                                  .filter((x) =>
                                    [
                                      "trash",
                                      "eye",
                                      "default",
                                      "pencil",
                                    ].includes(x.icon)
                                  )
                                  ?.map(
                                    ({
                                      id,
                                      label,
                                      color = "primary",
                                      onClick,
                                      disabled,
                                      icon,
                                    }) => (
                                      <Stack key={id}>
                                        {icon === "eye" && item?.isEdit && (
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => onClick(item)}
                                            sx={{
                                              px: 4,
                                              width: "auto",
                                              maxWidth: "fit-content",
                                              margin: "auto",
                                              py: 0.5,
                                              backgroundColor: `var(--black)`,
                                              color: "var(--white)",
                                              display: {
                                                xs: "inline-flex",
                                              },
                                            }}
                                            disabled={
                                              disabled ? disabled(item) : false
                                            }
                                            startIcon={
                                              icon
                                                ? getIconComponent(icon, color)
                                                : null
                                            }
                                          >
                                            {label}
                                          </Button>
                                        )}
                                        {icon === "trash" && item?.isDelete && (
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => onClick(item)}
                                            sx={{
                                              px: 3,
                                              backgroundColor: `var(--${color})`,
                                            }}
                                            disabled={
                                              disabled ? disabled(item) : false
                                            }
                                            startIcon={
                                              icon
                                                ? getIconComponent(icon, color)
                                                : null
                                            }
                                          >
                                            {label}
                                          </Button>
                                        )}
                                        {icon === "default" &&
                                          item?.isDefault && (
                                            <Button
                                              size="small"
                                              variant="contained"
                                              color="secondary"
                                              onClick={() => onClick(item)}
                                              sx={{
                                                px: 3,
                                                backgroundColor: `var(--${color})`,
                                              }}
                                              disabled={
                                                disabled
                                                  ? disabled(item)
                                                  : false
                                              }
                                              startIcon={
                                                icon
                                                  ? getIconComponent(
                                                      icon,
                                                      color
                                                    )
                                                  : null
                                              }
                                            >
                                              {label}
                                            </Button>
                                          )}
                                        {icon === "pencil" &&
                                          item?.isPencil && (
                                            <Button
                                              size="small"
                                              variant="contained"
                                              color="secondary"
                                              onClick={() => onClick(item)}
                                              sx={{
                                                px: 3,
                                                backgroundColor: `var(--${color})`,
                                              }}
                                              disabled={
                                                disabled
                                                  ? disabled(item)
                                                  : false
                                              }
                                              startIcon={
                                                icon
                                                  ? getIconComponent(
                                                      icon,
                                                      color
                                                    )
                                                  : null
                                              }
                                            >
                                              {label}
                                            </Button>
                                          )}
                                      </Stack>
                                    )
                                  )}
                            </Stack>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                );
              })}
            </Box>
          )}
          {!loading && data.length === 0 && (
            <Alert severity="info">{t("DataCardGrid.No data available")}</Alert>
          )}

          {/* Pagination */}
          {enablePagination && totalPages > 1 && (
            <Stack
              direction={"row"}
              gap={1}
              width={"100%"}
              alignItems={"center"}
              justifyContent={"space-between"}
              flexWrap={"wrap"}
            >
              <Pagination
                className="cm-pagination"
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                variant="filled"
                color="secondary"
                disabled={loading}
                shape="rounded"
              />
            </Stack>
          )}

          {enablePagination && (
            <Box textAlign="center" borderBottom={0} mt={"20px"}>
              <Typography variant="body2">
                {t("DataCardGrid.Showing")} {data.length} {t("DataCardGrid.Of")}{" "}
                {totalCount} {t("DataCardGrid.Records")}
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

DataCardGrid.propTypes = {
  initialData: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(["text", "number", "date", "boolean"]).isRequired,
      icon: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
    })
  ),
  title: PropTypes.string,
  defaultItemsPerPage: PropTypes.number,
  defaultColumnsPerGrid: PropTypes.number,
  enableExport: PropTypes.bool,
  enableFilters: PropTypes.bool,
  enablePagination: PropTypes.bool,
  enableSearch: PropTypes.bool,
  enableSorting: PropTypes.bool,
  enableGridConfig: PropTypes.bool,
  onDataChange: PropTypes.func,
  defaultSortKey: PropTypes.string,
  defaultSortDirection: PropTypes.oneOf(["asc", "desc"]),
  exportFileName: PropTypes.string,
  uniqueId: PropTypes.string,
  filterableColumns: PropTypes.arrayOf(PropTypes.string),
  actionButtons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      color: PropTypes.string,
      onClick: PropTypes.func.isRequired,
    })
  ),
  onFetchData: PropTypes.func,
};
export default DataCardGrid;
